import React, { ChangeEvent, useCallback, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { Button, Drawer } from "@mui/material";
import { SimpleHead } from "components";
import { Routes } from "constants/routes";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useFormatDateHook, useNavigate } from "hooks";
import { IApiServiceError, useCreateTaskSelect, useDealIdSelector, useTaskManageActions } from "store";
import { ICreateTaskReq } from "store/types";
import apiErrorsToFormik from "utils/api-errors-to-formik";
import { onlyChangedValues } from "utils/forms";
import rmEmptyFields from "utils/rm-empty-fields";

import { taskStatuses } from "../../../constants";
import { Attachments } from "../Attachments/Attachments";
import { FieldsSet, FieldsState } from "../Fields-set/FieldsSet";

import "./Create.styles.scss";

const initialFields: FieldsState = {
  title: "",
  deal: null,
  assignee: null,
  status: { label: taskStatuses.open, value: "open" },
  priority: { label: "Medium", value: "medium" },
  due_date: null,
  description: null
};

export const Create: React.FC = () => {
  const { isOpen, entity_id, entity_type, disableRedirectOnSubmit } = useCreateTaskSelect();
  const dealId = useDealIdSelector();
  const formatDate = useFormatDateHook();
  const { pathname } = useLocation();
  const isTaskManage = matchPath(pathname, Routes.dealTaskManage);

  const { setCreateAction, createTaskAction } = useTaskManageActions();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  const addFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];
      if (file) {
        setAttachments([...attachments, file]);
      }
    },
    [attachments]
  );

  const removeFreshFile = useCallback(
    (date: number) => {
      setAttachments(attachments.filter((item) => item.lastModified !== date));
    },
    [attachments]
  );

  const { values, setFieldValue, resetForm, setErrors, errors } = useFormik({
    initialValues: initialFields,
    onSubmit: () => {},
    validateOnChange: false
  });

  const onClose = () => {
    resetForm();
    setAttachments([]);
    setCreateAction({ isOpen: false });
  };

  const onSubmit = useCallback(async () => {
    if (loading) return;
    try {
      setLoading(true);
      const dataToSend: ICreateTaskReq = { files: attachments };
      const { deal, assignee, status, due_date, priority, ...restData } = rmEmptyFields(
        onlyChangedValues(values, initialFields)
      );
      if (entity_id && entity_type) {
        // Map UI-level entity_type to backend fields:
        // - "client" → set client_id (NOT entity_type)
        // - "deal" → set deal_id (NOT entity_type)
        // - All others (section, form, file, note, question, document) → set entity_type + entity_id
        if (entity_type === "client") {
          dataToSend.client_id = entity_id;
        } else if (entity_type === "deal") {
          dataToSend.deal_id = entity_id;
        } else {
          dataToSend.entity_id = entity_id;
          dataToSend.entity_type = entity_type;
        }
      }
      if (dealId) {
        dataToSend.deal_id = dealId;
      }
      if (deal) {
        dataToSend.deal_id = deal.id;
      }
      if (assignee) {
        dataToSend.assignee_id = assignee.id;
      }
      if (status) {
        dataToSend.status = status.value;
      }
      if (priority) {
        dataToSend.priority = priority.value;
      }
      if (due_date) {
        dataToSend.due_date = formatDate({ date: dayjs(due_date).toISOString(), format: "YYYY-MM-DD" });
      }
      // Typescript issues, realy hate Object.keys or "for of" with TS
      Object.keys(restData).forEach((key) => {
        if ((restData as any)[key]) {
          (dataToSend as any)[key] = (restData as any)[key];
        }
      });

      await createTaskAction(dataToSend);
      if (!disableRedirectOnSubmit && !isTaskManage && dealId) {
        navigate({ route: "dealTaskManage", params: { deal_id: dealId } });
      }
      onClose();
    } catch (error) {
      setErrors(apiErrorsToFormik(initialFields, (error as IApiServiceError).errors));
    } finally {
      setLoading(false);
    }
  }, [values, attachments, isTaskManage, dealId, loading]);

  return (
    <Drawer sx={{ ".MuiPaper-root": { borderRadius: "0 25px 25px 0" } }} open={isOpen} onClose={onClose}>
      <div className="task-drawer">
        <SimpleHead onIconClick={onClose}>Add Task</SimpleHead>
        <div className="body">
          <FieldsSet
            setFieldValue={setFieldValue}
            isStatusDisabled
            values={values}
            // menu={<Menu onAddFile={addFile} icon={<AttachFileIcon />}/>}
            errors={errors}
            setErrors={setErrors}
            statusAndPriorityInRow
          />
          <Attachments onAddFile={addFile} freshItems={attachments} removeFreshFile={removeFreshFile} />
        </div>
        <div className="footer">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} type="submit" disabled={loading || !values.title} variant="contained">
            Save
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
